import { useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, UserCog, Mail } from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { QUOTE_PERMISSION_DEFAULTS, QUOTE_PERMISSION_KEYS } from '@/lib/quotePermissions';
import { showApiError } from '@/lib/apiErrors';
import { useQuoteEntitlement } from '@/hooks/useQuoteEntitlement';
import { PlanLockBadge } from '@/components/quotes/PlanLockBadge';

const API_URL = process.env.REACT_APP_API_URL;
// Placeholder staff created by the old appointment screen cannot log in.
const isPlaceholder = (s) => (s.email || '').endsWith('@no-email.paydaai.local');

function effectivePerms(perms) {
  const out = {};
  QUOTE_PERMISSION_KEYS.forEach((k) => {
    out[k] = perms && Object.prototype.hasOwnProperty.call(perms, k) ? !!perms[k] : QUOTE_PERMISSION_DEFAULTS[k];
  });
  return out;
}

function emptyForm() {
  return { name: '', email: '', phone: '', password: '', permissions: { ...QUOTE_PERMISSION_DEFAULTS } };
}


export function QuoteTeam() {
  const { t } = useTranslation('quotes');
  const { entitlement, refresh: refreshEntitlement } = useQuoteEntitlement();
  const teamLocked = entitlement?.limits?.team_management === false;
  const memberLimit = entitlement?.limits?.team_member_limit ?? null;
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // staff row or 'new'
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const memberCount = entitlement?.usage?.team_member_count ?? (staff.length + 1);
  const atLimit = !teamLocked && memberLimit !== null && memberCount >= memberLimit;

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/staff`);
      setStaff((data || []).filter((s) => !isPlaceholder(s)));
    } catch (err) {
      showApiError(err, t);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openNew = () => {
    setForm(emptyForm());
    setEditing('new');
  };

  const openEdit = (s) => {
    setForm({
      name: s.name || '',
      email: s.email || '',
      phone: s.phone || '',
      password: '',
      permissions: effectivePerms(s.permissions),
      original: s.permissions || {},
    });
    setEditing(s);
  };

  const togglePerm = (key, value) => {
    setForm((f) => ({ ...f, permissions: { ...f.permissions, [key]: value } }));
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error(t('team.validation.name'));
      return;
    }
    if (form.password && form.password.length < 6) {
      toast.error(t('team.validation.password'));
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        if (!form.email.trim()) {
          toast.error(t('team.validation.email'));
          setSaving(false);
          return;
        }
        await axios.post(`${API_URL}/api/staff`, {
          name: form.name.trim(),
          email: form.email.trim(),
          phone: form.phone.trim() || undefined,
          password: form.password || undefined,
          permissions: form.permissions,
        });
        toast.success(t('team.invitedToast', { email: form.email.trim() }));
      } else {
        await axios.patch(`${API_URL}/api/staff/${editing.id}`, {
          name: form.name.trim(),
          phone: form.phone.trim(),
          password: form.password || undefined,
          // keep the old appointment-module keys as they were
          permissions: { ...(form.original || {}), ...form.permissions },
        });
        toast.success(t('common.saved'));
      }
      setEditing(null);
      load();
      refreshEntitlement();
    } catch (err) {
      showApiError(err, t);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(t('team.deleteConfirm', { name: s.name }))) return;
    try {
      await axios.delete(`${API_URL}/api/staff/${s.id}`);
      toast.success(t('common.deleted'));
      load();
      refreshEntitlement();
    } catch (err) {
      showApiError(err, t);
    }
  };

  return (
    <QuoteLayout>
      <div className="max-w-4xl mx-auto" data-testid="quote-team-page">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#1A1A2E]">{t('team.title')}</h1>
            <p className="text-sm text-[#595959] mt-1">{t('team.subtitle')}</p>
            {!teamLocked && memberLimit !== null && (
              <p className="mt-1 text-xs text-[#475569]" data-testid="team-usage">{t('team.usage', { count: memberCount, limit: memberLimit })}</p>
            )}
          </div>
          <Button onClick={openNew} disabled={teamLocked || atLimit} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="team-new-btn">
            <Plus className="w-4 h-4 mr-2" /> {t('team.newButton')}
            {teamLocked && <PlanLockBadge plan="Business" />}
          </Button>
        </div>

        {teamLocked && (
          <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="team-upsell">
            <p className="font-semibold text-amber-900">{t('team.upsell.title')}</p>
            <p className="mt-1 text-sm text-amber-900">{t('team.upsell.body')}</p>
            <a href="/quote-upgrade" className="mt-2 inline-block text-sm font-semibold text-[#2563EB] underline">{t('team.upsell.cta')}</a>
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#E5E5E5] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#595959]">{t('common.loading')}</div>
          ) : staff.length === 0 ? (
            <div className="p-10 text-center" data-testid="team-empty">
              <UserCog className="w-10 h-10 mx-auto text-[#94A3B8] mb-3" />
              <p className="text-[#475569] mb-4">{t('team.empty')}</p>
              {!teamLocked && (
                <Button onClick={openNew} disabled={atLimit} className="bg-[#2563EB] hover:bg-[#1E4ED8]">
                  <Plus className="w-4 h-4 mr-2" /> {t('team.newButton')}
                </Button>
              )}
            </div>
          ) : (
            <ul className="divide-y divide-[#F1F5F9]">
              {staff.map((s) => {
                const perms = effectivePerms(s.permissions);
                const on = QUOTE_PERMISSION_KEYS.filter((k) => perms[k]);
                return (
                  <li key={s.id} className="px-4 py-3 flex items-start gap-3" data-testid={`team-row-${s.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[#1A1A2E]">{s.name}</div>
                      <div className="text-xs text-[#94A3B8] truncate">{s.email}{s.phone ? ` · ${s.phone}` : ''}</div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {on.length === 0 ? (
                          <span className="text-xs text-amber-700">{t('team.noPermissions')}</span>
                        ) : on.map((k) => (
                          <span key={k} className="rounded-full bg-blue-50 px-2 py-0.5 text-[11px] text-[#1E4ED8]">
                            {t(`team.perms.${k}.title`)}
                          </span>
                        ))}
                      </div>
                    </div>
                    <Button size="icon" variant="ghost" onClick={() => openEdit(s)} title={t('common.edit')} data-testid={`team-edit-${s.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(s)} title={t('common.delete')} className="text-red-600">
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <p className="mt-3 text-xs text-[#94A3B8]">{t('team.ownerNote')}</p>
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing === 'new' ? t('team.newTitle') : t('team.editTitle')}</DialogTitle>
            {editing === 'new' && (
              <DialogDescription className="flex gap-2">
                <Mail className="w-4 h-4 shrink-0 mt-0.5" /> {t('team.inviteHelp')}
              </DialogDescription>
            )}
          </DialogHeader>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <Label>{t('team.form.name')}</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name-input" />
            </div>
            <div>
              <Label>{t('team.form.email')}</Label>
              <Input
                type="email"
                value={form.email}
                disabled={editing !== 'new'}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                data-testid="team-email-input"
              />
            </div>
            <div>
              <Label>{t('team.form.phone')}</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label>{editing === 'new' ? t('team.form.password') : t('team.form.newPassword')}</Label>
              <Input
                type="password"
                autoComplete="new-password"
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
                placeholder={t('team.form.passwordPlaceholder')}
              />
            </div>
          </div>

          <div className="mt-2">
            <p className="text-sm font-semibold text-[#1A1A2E] mb-2">{t('team.form.permissions')}</p>
            <ul className="divide-y divide-[#F1F5F9] rounded-xl border border-[#E5E5E5]">
              {QUOTE_PERMISSION_KEYS.map((k) => (
                <li key={k} className="flex items-center gap-3 px-3 py-2.5">
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-[#1A1A2E]">{t(`team.perms.${k}.title`)}</div>
                    <div className="text-xs text-[#94A3B8]">{t(`team.perms.${k}.desc`)}</div>
                  </div>
                  <Switch
                    checked={!!form.permissions[k]}
                    onCheckedChange={(v) => togglePerm(k, v)}
                    data-testid={`team-perm-${k}`}
                  />
                </li>
              ))}
            </ul>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{t('common.cancel')}</Button>
            <Button onClick={submit} disabled={saving} className="bg-[#2563EB] hover:bg-[#1E4ED8]" data-testid="team-save-btn">
              {editing === 'new' ? t('team.inviteButton') : t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </QuoteLayout>
  );
}
